/**
 * HTML可なplaceholderを表示するコンポーネントの共通mixin
 */
export default {
    data() {
        return {
            // フォーカスフラグ
            focus: false,
            // 編集中の内容
            edit_value: '',
        };
    },
    props: {
        // テキストエリア内容
        modelValue: {
            require: true
        },
        // バリデーションパターン
        pattern: {
            require: false,
            default: ''
        },
        // type指定
        type: {
            type: String,
            require: false,
            default: 'text'
        },
        // 全角最大文字数
        full_max_width: {
            type: Number,
            require: false
        },
        // 半角最大文字数
        half_max_width: {
            type: Number,
            require: false
        },
        // 最大値
        max_num: {
            type: Number,
            require: false
        },
        // 最小値
        min_num: {
            type: Number,
            require: false
        },
        // エンター改行の<br>変換分文字数を足すフラグ
        enter_replace: {
            type: Boolean,
            require: false,
        },
        // 入力必須か否かの指定
        required: {
            type: Boolean,
            require: false,
        },
        // 全角カタカナ
        full_katakana: {
            type: Boolean,
            require: false,
        },
        // 半角数字
        half_number: {
            type: Boolean,
            require: false
        },
        // yahoo!に登録できる画像フラグ
        yahoo_img: {
            type: Boolean,
            require: false
        },
        // 高さ指定
        height: {
            type: Number,
            require: false
        },
        // 文字数カウント表示フラグ
        show_count: {
            type: Boolean,
            require: false
        },
    },
    computed: {
        _input: function() {
            return (this.$refs['textinput'] ?? this.$refs['textarea']);
        },
        // プレースホルダを表示
        show: function() {
            if (!this.edit_value && !this.focus) {
                return true;
            }
            return false;
        },
        // 文字数を返す
        valueLength: function() {
            let length = 0;
            for (let i=0; i<this.edit_value.length; i++) {
                if (this.edit_value[i].match(/[ -~\n]/) ) {
                    length += 1;
                } else {
                    length += 2;
                }
                // エンター改行を<br>\nとヤフー反映時に出力する為、<br>分文字数を足す
                if (this.enter_replace && this.edit_value[i] === '\n') {
                    length += 4;
                }
            }
            if (this.full_max_width) {
                return length / 2;
            }
            return length;
        },
    },
    watch: {
        // HTML5カスタムメッセージと編集変数を更新する
        modelValue: {
            async handler(newValue, oldValue) {
                // 初回(oldValueがundefined)はdom更新を待つ
                if (typeof oldValue === 'undefined') {
                    await this.$nextTick();
                }
                this.setEditValue();
                this.setCustomValidity();
            },
            immediate: true,
        },
        // フォーカスの切替
        focus: function() {
            if (this.focus) {
                this.setEditValue();
                this._input.focus();
            } else {
                this.emit();
                this._input.blur();
            }
        },
    },
    emits: ['update:modelValue', 'update-valid'],
    methods: {
        // フォーカスの切り替え
        setFocus: function (bool) {
            this.focus = bool;
        },
        // 編集用変数に代入する
        setEditValue: function () {
            this.edit_value = (this.modelValue !== null) ? this.modelValue : '';
        },
        // カスタムメッセージをセットする
        setCustomValidity: function (message = '') {
            if (this.isOver()) {
                message = '文字数を減らしてください';
            }
            if (!this.isMatchValue(this.full_katakana, /^[ァ-ヶー　]+$/)) {
                message = '全角カタカナで入力してください';
            }
            if (!this.isMatchValue(this.half_number, /^[0-9]+$/)) {
                message = '半角数値で入力してください';
            }
            if (!this.isYahooImg()) {
                message = 'ヤフーで登録できるURLではありません';
            }
            if (this.type === 'email' && !this.isMatchValue(true, /[\w\-._]+@[\w\-._]+\.[A-Za-z](?!.* )/)) {
                message = 'メールアドレス形式が不正です';
            }
            this._input.setCustomValidity(message);
        },
        // 文字数オーバーしているか
        isOver: function() {
            // 空、nullはチェックしない
            if (!this.edit_value) {
                return false;
            }
            if (this.full_max_width && this.valueLength > this.full_max_width) {
                return true;
            }
            if (this.half_max_width && this.valueLength > this.half_max_width) {
                return true;
            }
            return false;
        },
        // ストアクリエイター内の画像かトリプルのURLか
        isYahooImg: function () {
            // 空、nullはtrue
            if (!this.yahoo_img || !this.edit_value) {
                return true;
            }
            // 商品画像
            if (new RegExp('^https://item-shopping.c.yimg.jp.*$').test(this.edit_value)) {
                return true;
            }
            // 追加画像フォーマット
            if (new RegExp('^https://shopping.c.yimg.jp/lib/.*$').test(this.edit_value)) {
                return true;
            }
            // トリプル配下
            if (new RegExp('^https://shopping.geocities.jp.*.(jpg|jpeg|gif|png)$').test(this.edit_value)) {
                return true;
            }
            // yahoo提供画像フォーマット
            if (new RegExp('^https://s.yimg.jp/images/.*.(jpg|jpeg|gif|png)$').test(this.edit_value)) {
                return true;
            }
            return false;
        },
        // 入力内容が指定の正規表現にマッチしているか
        isMatchValue: function (boolean, reg) {
            // 空、nullはtrue
            if (!boolean || !this.edit_value) {
                return true;
            }
            if (reg.test(this.edit_value)) {
                return true;
            }
            return false;
        },
        // エラーイベントをリセット
        resetError(event) {
            event.target.setCustomValidity('');
        },

        // -------------------------
        // emit
        // -------------------------

        // フォームの内容を更新したら
        emit: function() {
            // 変更をvueへ通知
            this.$emit('update:modelValue', this.edit_value);
            // 文字数オーバー等のバリデーション結果を通知
            this.$nextTick(() => {
                this.$emit('update-valid', this._input.checkValidity());
            });
        }
    }
};
